import * as React from "react";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import { useQuery } from "react-query";
import { ApiError, ExternalIdentityProvider, UtilsService } from "gen/clients";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { DataTable } from "components/DataTable/DataTable";

const QUERY_KEY = "listExternalIdentityProviders";

interface Props {
  onClose: () => void;
}

const SsoProvidersPanel: React.FC<Props> = ({ onClose }) => {
  const { data, isLoading, error } = useQuery<ExternalIdentityProvider[], ApiError>([QUERY_KEY], {
    queryFn: UtilsService.listExternalIdentityProviders,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });

  const providers = React.useMemo(
    () =>
      [...(data || [])].sort((provider1, provider2) => provider1.providerName.localeCompare(provider2.providerName)),
    [data]
  );

  const rowKey = React.useCallback(
    (provider: ExternalIdentityProvider) => `${provider.providerName}-${provider.providerType || "unknown"}`,
    []
  );

  return (
    <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: "60vw", maxWidth: 1000 } }}>
      <DrawerHeader title="SSO Providers" onClose={onClose} />
      <DrawerBody isLoading={isLoading} error={error}>
        <DataTable
          columns={[
            {
              id: "providerName",
              title: "Name",
              cell: (provider: ExternalIdentityProvider) => provider.providerName
            },
            {
              id: "providerType",
              title: "Type",
              cell: (provider: ExternalIdentityProvider) => provider.providerType || "—"
            },
            {
              id: "idpIdentifiers",
              title: "Email Domains",
              cell: (provider: ExternalIdentityProvider) => provider.idpIdentifiers?.join(", ") || "—"
            }
          ]}
          data={providers}
          rowKey={rowKey}
          dense={true}
          noData={<Typography color="text.secondary">No SSO providers found.</Typography>}
        />
      </DrawerBody>
    </Drawer>
  );
};

export { SsoProvidersPanel };
